import { Action } from '@reduxjs/toolkit'
import debounce from 'debounce'
import { produceWithPatches } from 'immer'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useImmer } from 'use-immer'

import { useAppDispatch } from '@/hooks'
import { useDetailTabContext } from '@/pages/User/pages/Home/components/DetailTab'
import { StructureDto } from '@/rtkApi/mddApiSliceGenerated'
import { AppDispatch, RootState } from '@/store'
import { UpdateDeepPartial } from '@/store/utils'
import { ColumnVisibility } from '@/types'
import { normalizeTableData } from '@/utils'
import { renumberOrderInPlace } from '@/utils/collectionOrder'
import { logError } from '@/utils/errorHandling'

export type UpdateCellValueHandler<T> = <K extends keyof T>(
	rowIndex: number,
	columnId: K,
	value: T[K],
) => void

export type CustomDeleteRowHandler = (
	deletedRowId: number | string,
	defaultDelete: () => void,
) => void

export type CustomRowClickHandler<TData> = (
	rowData: TData,
	defaultHandler: (rowData: TData) => void,
) => void

export type CustomUpdateSelectedRowHandler<TData> = (updatedRow: TData) => void

export interface UseTableHandlersProps<TData> {
	columnInitVisibility?: ColumnVisibility
	customDeleteRow?: CustomDeleteRowHandler
	customRowClick?: CustomRowClickHandler<TData>
	customUpdateCellValue?: UpdateCellValueHandler<TData>
	customUpdateSelectedRow?: CustomUpdateSelectedRowHandler<TData>
	debounceTime?: number
	initialData: TData[]
	/** V2 callback mode - called when data changes instead of Redux dispatch */
	onDataChange?: (data: TData[]) => void,
	/** Redux actions for legacy mode - optional if onDataChange is provided */
	reduxActions?: {
		saveAction: (
			node: StructureDto,
		) => (dispatch: AppDispatch, getState: () => RootState) => Promise<unknown>
		updateAction: (node: StructureDto, update: UpdateDeepPartial<any>) => Action
		updateKey: string
	}
}

const deepClonePlain = <T>(value: T): T => {
	try {
		const structuredCloneFn = globalThis?.structuredClone
		if (structuredCloneFn) return structuredCloneFn(value) as T
		return JSON.parse(JSON.stringify(value))
	} catch {
		return JSON.parse(JSON.stringify(value))
	}
}

export const useTableHandlers = <TData extends { id?: number; uuid?: string }>({
	initialData,
	debounceTime = 500,
	columnInitVisibility = {
		name: true,
	},
	customDeleteRow,
	customUpdateCellValue,
	customUpdateSelectedRow,
	reduxActions,
	customRowClick,
	onDataChange,
}: UseTableHandlersProps<TData>) => {
	const {
		state: { node },
	} = useDetailTabContext()
	const dispatch = useAppDispatch()
	const [columnVisibility, setColumnVisibility] =
		useState<ColumnVisibility>(columnInitVisibility)
	const [tableData, setTableData] = useImmer(
		normalizeTableData<TData>(initialData),
	)
	// Keep a plain snapshot ref to avoid mixing Immer drafts
	const tableDataRef = useRef<TData[]>(tableData)

	useEffect(() => {
		tableDataRef.current = tableData as TData[]
	}, [tableData])

	// Start with no selection so an editor/panel opened later highlights the
	// row the user actually interacts with, not the first row by default.
	const [selectedRow, setSelectedRow] = useState<TData | null>(null)

	const isUserInteracting = useRef(false)

	// Support both Redux mode and callback mode
	const isCallbackMode = !!onDataChange && !reduxActions
	const { saveAction, updateAction, updateKey } = reduxActions ?? {}

	const saveDebounced = useCallback(
		debounce(async () => {
			if (isCallbackMode) {
				// In callback mode, data is already synced via onDataChange
				return
			}
			try {
				if (saveAction) {
					await dispatch(saveAction(node))
				}
			} catch (e) {
				logError(e, { operation: 'useTableHandlers.saveDebounced' })
			}
		}, debounceTime),
		[dispatch, node, saveAction, debounceTime, isCallbackMode],
	)

	/** Run any pending debounced save immediately (focus loss / leave). */
	const flushSave = useCallback(() => {
		if (isCallbackMode) {
			return
		}
		saveDebounced.flush()
	}, [isCallbackMode, saveDebounced])

	const updateTableData = useCallback(
		(updater: (draft: TData[]) => void) => {
			const base = tableDataRef.current as TData[]
			const [next, patches] = produceWithPatches(base, (draft) => {
				updater(draft as TData[])
			})
			if (patches.length === 0) return

			// Keep ref in sync immediately so row-switch flush / nested updates
			// never rebuild from a stale snapshot that omits a just-added row.
			tableDataRef.current = next as TData[]
			setTableData(next as TData[])

			if (isCallbackMode && onDataChange) {
				// V2 callback mode - notify parent of changes
				onDataChange(deepClonePlain(next) as TData[])
			} else if (updateAction && updateKey) {
				// Legacy Redux mode
				dispatch(updateAction(node, { [updateKey]: deepClonePlain(next) }))
				saveDebounced()
			}
		},
		[dispatch, node, saveDebounced, setTableData, updateAction, updateKey, isCallbackMode, onDataChange],
	)

	const defaultUpdateCellValue = useCallback(
		(rowIndex: number, columnId: keyof TData, value: unknown) => {
			// Without this check on first render the logic would assign undefined to some dropdowns
			if (!isUserInteracting.current || value === undefined || value === null)
				return

			updateTableData((draft) => {
				const row = draft[rowIndex]
				if (!row) return
				const currentValue = row[columnId]
				if (currentValue === value) return
				row[columnId] = value as TData[keyof TData]
			})
		},
		[updateTableData],
	)

	const updateCellValue = useCallback(
		(rowIndex: number, columnId: keyof TData, value: TData[keyof TData]) => {
			if (customUpdateCellValue) {
				customUpdateCellValue(rowIndex, columnId, value)
			} else {
				defaultUpdateCellValue(rowIndex, columnId, value)
			}
		},
		[customUpdateCellValue, defaultUpdateCellValue],
	)

	const defaultUpdateSelectedRow = useCallback(
		(updatedRow: TData) => {
			updateTableData((draft) => {
				const rowIndex = draft.findIndex((row) => row.id === updatedRow.id)
				if (rowIndex !== -1) {
					const currentRow = draft[rowIndex]
					const hasChanges = Object.keys(updatedRow).some(
						(key) =>
							currentRow[key as keyof TData] !== updatedRow[key as keyof TData],
					)

					if (hasChanges) {
						draft[rowIndex] = { ...draft[rowIndex], ...updatedRow }
					}
				}
			})
		},
		[updateTableData],
	)

	const updateSelectedRow = useCallback(
		(updatedRow: TData) => {
			if (customUpdateSelectedRow) {
				customUpdateSelectedRow(updatedRow)
			} else {
				defaultUpdateSelectedRow(updatedRow)
			}
		},
		[customUpdateSelectedRow, defaultUpdateSelectedRow],
	)

	const defaultDeleteRow = useCallback(
		(deletedRowId: number | string) => {
			if (!isUserInteracting.current) return
			updateTableData((draft) => {
				const idx = draft.findIndex((row) => row.id === deletedRowId)
				if (idx !== -1) draft.splice(idx, 1)
				renumberOrderInPlace(draft as { order?: number }[])
			})
		},
		[updateTableData],
	)

	const deleteRow = useCallback(
		(deletedRowId: number | string) => {
			if (customDeleteRow) {
				customDeleteRow(deletedRowId, () => defaultDeleteRow(deletedRowId))
			} else {
				defaultDeleteRow(deletedRowId)
			}
		},
		[customDeleteRow, defaultDeleteRow],
	)

	const defaultHandleRowClick = useCallback((rowOriginal: TData) => {
		setSelectedRow(rowOriginal)
	}, [])

	const handleRowClick = useCallback(
		(rowOriginal: TData) => {
			if (customRowClick) {
				customRowClick(rowOriginal, defaultHandleRowClick)
			} else {
				defaultHandleRowClick(rowOriginal)
			}
		},
		[customRowClick, defaultHandleRowClick],
	)

	return {
		columnVisibility,
		deleteRow,
		flushSave,
		handleRowClick,
		isUserInteracting,
		selectedRow,
		setColumnVisibility,
		setSelectedRow,
		setTableData,
		tableData,
		updateCellValue,
		updateSelectedRow,
		updateTableData,
		saveDebounced,
	}
}
